import React, { useEffect, useState } from 'react';
import styles from './ChatList.module.css';
import roomImg from '../../../assets/pic/chatlist_pic.png'; // Fallback image or default
import searchIcon from '../../../assets/pic/search.svg';
import { useNavigate } from 'react-router-dom';
import { fetchChatRoomsSummary } from '../../../apis/chatApi';
import useDataStore from '../../../stores/useDataStore';
import resolveMediaUrl from '../../../utils/resolveMediaUrl';

const ParticipatingChats = () => {
    const navigate = useNavigate();
    const chatRooms = useDataStore((s) => s.chatRooms);
    const setChatRooms = useDataStore((s) => s.setChatRooms);

    // Removed specific timestamp check for now to ensure badge updates, 
    // or we can rely on manual invalidation.
    // Let's perform fresh fetch on mount to get badge.

    // 1. Polling for real-time unread counts()
    useEffect(() => {
        let isMounted = true;

        const fetchRooms = async () => {
            try {
                if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
                const data = await fetchChatRoomsSummary();
                if (process.env.NODE_ENV === 'development') {
                    console.log('ChatRooms Data:', data);
                }
                if (isMounted && Array.isArray(data)) {
                    setChatRooms(data);
                }
            } catch (error) {
                if (process.env.NODE_ENV === 'development') {
                    console.error("Polling failed", error);
                }
            }
        };

        // Initial fetch
        fetchRooms();

        // Poll with requestIdleCallback to avoid blocking main thread
        const schedulePolling = () => {
            if ('requestIdleCallback' in window) {
                window.requestIdleCallback(() => {
                    if (isMounted) fetchRooms();
                }, { timeout: 5000 });
            } else {
                // Fallback: use setTimeout to defer execution
                setTimeout(() => {
                    if (isMounted) fetchRooms();
                }, 0);
            }
        };

        const intervalId = setInterval(schedulePolling, 10000);

        return () => {
            isMounted = false;
            clearInterval(intervalId);
        };
    }, [setChatRooms]);

    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 5;
    const activeChatRooms = React.useMemo(() => chatRooms || [], [chatRooms]);

    // 정렬 로직: 읽지 않은 메시지 > lastMessageTime (백엔드 제공) > roomId 순
    const sortedChatRooms = React.useMemo(() => {
        return [...activeChatRooms].sort((a, b) => {
            // 1순위: 읽지 않은 메시지가 있는 방 우선
            const unreadA = a.unreadCount || 0;
            const unreadB = b.unreadCount || 0;
            if (unreadA > 0 && unreadB === 0) return -1;
            if (unreadB > 0 && unreadA === 0) return 1;

            // 2순위: lastMessageTime (백엔드에서 제공, 최신순)
            const timeA = a.lastMessageTime ? new Date(a.lastMessageTime).getTime() : (a.roomId || 0);
            const timeB = b.lastMessageTime ? new Date(b.lastMessageTime).getTime() : (b.roomId || 0);
            return timeB - timeA;
        });
    }, [activeChatRooms]);

    const totalPages = Math.ceil(sortedChatRooms.length / ITEMS_PER_PAGE);

    const displayedRooms = React.useMemo(() => {
        return sortedChatRooms.slice(
            (currentPage - 1) * ITEMS_PER_PAGE,
            currentPage * ITEMS_PER_PAGE
        );
    }, [sortedChatRooms, currentPage]);



    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleRoomClick = (roomId, unreadCount = 0) => {
        navigate('/chatroom', { state: { roomId, unreadCount } });
    };

    const renderLastMessage = (room) => {
        // ✅ summary API에서 제공하는 lastMessagePreview 우선 사용 (성능 최적화)
        let text = room.lastMessagePreview;

        if (!text) {
            // fallback: 다른 필드명으로 제공될 경우
            text =
                room.lastMessage ||
                room.lastChatMessage ||
                room.lastMsg ||
                room.lastMessageText ||
                room.lastMessageContent ||
                room.lastMessageBody;
        }

        if (!text) return '채팅방에 참여하였습니다.';

        // ::AI_PLAN:: 메시지인 경우 보기 좋게 변환
        if (typeof text === 'string' && text.startsWith('::AI_PLAN::')) {
            return '🗺️ AI 여행 일정이 생성되었습니다';
        }

        return text;
    };

    return (
        <div className={styles.participatingContainer}>
            <div className={styles.sectionHeader}>
                <span className={styles.sectionTitle}>참여중인 여행톡</span>
                <img
                    className={styles.searchIcon}
                    src={searchIcon}
                    alt="검색"
                    onClick={() => navigate('/chat/search')}
                    style={{ cursor: 'pointer' }}
                />
            </div>

            {activeChatRooms.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '14px' }}>
                    참여 중인 채팅방이 없습니다.
                </div>
            ) : (
                <>
                    {displayedRooms.map((room) => (
                        <div
                            key={room.roomId || room.room_id || Math.random()}
                            className={styles.roomCard}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleRoomClick(room.roomId || room.id, room.unreadCount || 0)}
                        >
                            <img
                                className={styles.roomImage}
                                src={resolveMediaUrl(room.imageUrl) || roomImg}
                                alt="채팅방 이미지"
                                onError={(e) => {
                                    console.warn('[room image load failed]', {
                                        roomId: room.roomId || room.id,
                                        imageUrl: room.imageUrl,
                                        resolved: resolveMediaUrl(room.imageUrl),
                                    });
                                    e.target.src = roomImg;
                                }} // Fallback on error
                            />
                            <div className={styles.roomContent}>
                                <div className={styles.roomTopRow}>
                                    <span className={styles.roomTitle}>{room.name || '알 수 없는 대화방'}</span>
                                    <span className={styles.roomTime}>{/* Time if available */}</span>
                                </div>
                                <div className={styles.roomMessageRow}>
                                    <div className={styles.roomMessage}>
                                        {renderLastMessage(room)}
                                    </div>
                                    {room.unreadCount > 0 && (
                                        <span className={styles.roomUnreadBadge}>
                                            {room.unreadCount > 99 ? '99+' : room.unreadCount}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className={styles.paginationContainer}>
                            <button
                                className={styles.pageButton}
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                &lt;
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                <button
                                    key={page}
                                    className={`${styles.pageButton} ${currentPage === page ? styles.active : ''}`}
                                    onClick={() => handlePageChange(page)}
                                >
                                    {page}
                                </button>
                            ))}
                            <button
                                className={styles.pageButton}
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            >
                                &gt;
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ParticipatingChats;
